import { CommonModule } from '@angular/common';
import { Component, computed, inject, ViewChild, ElementRef, OnInit, effect, HostListener } from '@angular/core';

import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { JobsService } from '../../data/services/jobs.service';
import { JobFormResponse } from '../../data/models/job-form.models';
import { JobProcessingStateService } from '../../data/services/job-processing-state.service';
import { environment } from '../../../../../environments/environmet';
import { CompanyContextService } from '../../../../core/services/company-context.service';
import { SessionService } from '../../../../core/services/session.service';
import { CompaniesService } from '../../../companies/data/services/companies.service';


@Component({
  selector: 'app-job-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './job-form.html',
  styleUrl: './job-form.scss',
})
export class JobForm implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly jobsService = inject(JobsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly jobProcessingState = inject(JobProcessingStateService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly sessionService = inject(SessionService);
  private readonly companiesService = inject(CompaniesService);


  @ViewChild('folderInput') folderInput!: ElementRef<HTMLInputElement>;

  isLoading = false;
  isSaving = false;
  errorMessage = '';
  infoMessage = '';

  folderValidationMessage = '';
  hasVerifiedFolder = false;
  selectedFolderName = '';

  showValidationAlert = false;
  validationAlertMessages: string[] = [];

  jobStatus = '';
  pricingPercentage = 80;
  showPricingDetails = false;
  activeTooltipKey: string | null = null;

  readonly futureProductionCapabilities: string[] = [
    'Vignetting',
    'Glasses Glare Removal',
    'Black & White',
    'Color Looks',
    'Backgrounds',
    'Special Edits',
    'Class Composites',
    'Other...',
  ];

  readonly futureImageDelivery: string[] = [
    'GotPhoto',
    'Captura',
    'Done.Photo',
    'WaldoPro',
    'SpotMyPhotos',
    'Other...',
  ];

  readonly tooltips: Record<string, string> = {
    totalImages:
      'This is the number of image files detected in the folder selected for this job.\n\nflwX uses this quantity when calculating applicable production quantities and estimated pricing.\n\nThis number is determined automatically from your selected job folder.',
    approximateSubjects:
      'Enter your best estimate of the number of individual subjects represented in this job.\n\nThis does not need to be exact. flwX uses this number to calculate the estimated average images per subject and other planning estimates.\n\nStaff, coaches, teachers, or other photographed individuals may be included when appropriate.',
    estimatedAverageImages:
      'This is a calculated estimate based on the total number of detected images divided by the approximate number of subjects you entered.\n\nFor example, 2,100 images ÷ 150 subjects = approximately 14 images per subject.\n\nActual image counts will vary. Some subjects may have more or fewer images because of multiple poses, sessions, outfits, staff photographs, groups, or other variations within the job.',
    productionServices:
      'Select the production services you want flwX to perform for this job.\n\nSome selected services may require additional options or configuration before the job is submitted.',
    smartCull:
      'Smart Cull reviews the uploaded images and selects the strongest images based on your preferences.\n\nThe goal is to create useful variety in expressions and poses while removing unnecessary duplicates and weaker images.\n\nYour settings tell the reviewer approximately what you want the finished selection for each subject to look like.',
    cullingMethod:
      'Choose how you want Smart Cull performed.\n\nProfessional Review is currently available and uses a person to review the images.\n\nAI Smart Cull is planned for a future release and will provide automated image selection based on your preferences.',
    professionalReview:
      'Every image is reviewed by a person to select the best images, expressions, and poses based on the preferences you provide.',
    aiSmartCull:
      'AI-powered automated image selection is planned for a future release.\n\nThis option is not currently available.',
    imagesToKeep:
      'Enter the approximate number of finished images you would like selected for each subject.\n\nThis is a target, not a guarantee. The actual number may vary depending on the number, quality, and variety of images available for an individual subject.\n\nImages that do not meet acceptable quality standards should not be selected simply to reach the target.',
    imagesPerPose:
      'Controls how many images Smart Cull may select from the same or substantially similar pose.\n\nChoose 1 when you want the strongest image from each unique pose. Choose a higher number when you want multiple expressions or variations from the same pose.\n\nFor example, if three images show essentially the same pose with different expressions, a setting of 1 tells Smart Cull to select the strongest one.',
    imagesPerTeacher:
      'Teachers, staff, coaches, and similar subjects often need fewer finished images than primary subjects.\n\nUse this setting to specify how many images you would normally like retained for these subjects when they can be identified within the job.',
    specialInstructions:
      'Use this field for unusual requirements or job-specific instructions that may affect image selection.\n\nNormal Smart Cull preferences should be configured using the settings above.',
    enhancement:
      'Enhancement applies professional image correction to the selected images before they continue through production.\n\nDepending on the selected preset, processing may include adjustments for exposure, color, contrast, clarity, skin tone, and other image characteristics.',
    backgroundRemoval:
      'Creates a clean subject extraction by separating the photographed subject from the original background.\n\nExtraction is intended for workflows requiring background replacement, composites, graphics, or other downstream production.',
    multiCropping:
      'Multi-Cropping creates multiple finished compositions from each selected image.\n\nInstead of requiring every composition to be photographed separately, flwX can create different crop variations from the same original image.',
    cropsPerImage:
      'Select how many finished crop variations you want created from each image entering Multi-Cropping.\n\nFor example, 1,200 images with 2 crops per image can produce up to approximately 2,400 finished compositions.\n\nActual output may vary when an image cannot support every requested crop.',
    futureCapabilities:
      'flwX is being expanded with additional production capabilities, including vignetting, glasses glare removal, black & white treatments, color looks, background options, special edits, class composites, and more.\n\nAs these capabilities become available, they will appear within the appropriate part of the flwX workflow.\n\nItems shown here are planned capabilities and are not currently available for production.',
    futureDelivery:
      'Direct delivery of finished images to gallery platforms is on the flwX roadmap.\n\nPlanned integrations shown here are future capabilities and are not currently available.\n\nAs gallery integrations become available, delivery options will be added to the appropriate part of the workflow.',
    estimatedProduction:
      'Your estimated flwX production total is calculated from the job information, selected services, and applicable production quantities.\n\nSome services are based on images uploaded, while others may be based on the actual number of images or compositions processed after earlier workflow steps.\n\nBecause production quantities can change as images move through the workflow, your final production total may be different from the original estimate.',
    estimatedProductionPerSubject:
      'This is the estimated total flwX production divided by the approximate number of subjects you entered.\n\nIt is provided as a planning tool and does not mean flwX bills the job at a fixed per-subject rate.',
    pricingDetails:
      'View the estimated charges associated with the production services selected for this job.\n\nEstimated quantities may change as images move through production. Final charges are based on the applicable services and actual production quantities.',
  };

  readonly companyId = computed(() => {
    if (this.sessionService.isSuperAdmin()) {
      return this.companyContextService.selectedCompanyId();
    }
    return this.sessionService.user()?.companyId ?? 0;
  });

  jobId = Number(this.route.snapshot.paramMap.get('id'));

  isEditMode(): boolean {
    return !!this.jobId && !Number.isNaN(this.jobId);
  }

  shouldShowNextButton(): boolean {
    if (!this.isEditMode()) {
      return true;
    }
    if (this.jobProcessingState.getActiveCreateJobId() === this.jobId) {
      return true;
    }
    return this.jobStatus === 'Draft' || this.jobStatus === 'ImageUploadCompleted';
  }

  readonly form = this.fb.group({
    name: ['', Validators.required],
    pickBestImage: [false],
    cropImages: [false],
    enhanceImages: [false],
    extractBackground: [false],

    noOfImagesInThisJob: [null as number | null, [Validators.required, Validators.min(0)]],
    estimatedStudentsPhotographed: [null as number | null, [Validators.required, Validators.min(1)]],
    avgFramesPhotographedPerStudent: [null as number | null, [Validators.min(0)]],
    desiredImagesPosesPerStudent: [null as number | null, [Validators.min(0)]],
    cropsPerImagePose: [null as number | null, [Validators.min(1), Validators.max(4)]],

    pickingCharge: [0, [Validators.min(0)]],
    cropCharge: [0, [Validators.min(0)]],
    enhancementCharge: [0, [Validators.min(0)]],
    extractionCharge: [0, [Validators.min(0)]],

    estimatedCostOfJob: [0],
    estimatedCostPerStudent: [0],
    upfrontCostOfJob: [0],

    imagesPerStudent: [null as number | null, [Validators.min(0)]],
    imagePerUniquePose: [null as number | null, [Validators.min(0)]],
    minimumImagesIfPossible: [null as number | null, [Validators.min(0)]],
    imagesPerTeacherIfPossible: [null as number | null, [Validators.min(0)]],
    specialInstructions: ['', Validators.maxLength(500)],
    isPaid: [false],
    paymentStatus: [''],
    transactionId: [''],
  });

  private updateCropsPerImagePoseValidator(cropImages: boolean): void {
    const cropsCtrl = this.form.get('cropsPerImagePose');
    if (cropsCtrl) {
      if (cropImages) {
        cropsCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(4)]);
      } else {
        cropsCtrl.setValidators([Validators.min(1), Validators.max(4)]);
        cropsCtrl.setValue(null, { emitEvent: false });
      }
      cropsCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  private updateSmartCullValidators(pickBestImage: boolean): void {
    const studentCtrl = this.form.get('imagesPerStudent');
    const poseCtrl = this.form.get('imagePerUniquePose');
    const teacherCtrl = this.form.get('imagesPerTeacherIfPossible');

    const updateCtrl = (ctrl: AbstractControl | null) => {
      if (ctrl) {
        if (pickBestImage) {
          ctrl.setValidators([Validators.required, Validators.min(0)]);
        } else {
          ctrl.setValidators([Validators.min(0)]);
        }
        ctrl.updateValueAndValidity({ emitEvent: false });
      }
    };

    updateCtrl(studentCtrl);
    updateCtrl(poseCtrl);
    updateCtrl(teacherCtrl);
  }

  private initialCompanyId: number | null = null;

  constructor() {
    this.form.get('cropImages')?.valueChanges.subscribe((cropImagesChecked) => {
      this.updateCropsPerImagePoseValidator(!!cropImagesChecked);
    });

    this.form.get('pickBestImage')?.valueChanges.subscribe((pickBestChecked) => {
      this.updateSmartCullValidators(!!pickBestChecked);
    });

    this.form.valueChanges.subscribe(() => {
      this.calculateSummary();
    });

    effect(() => {
      const cid = this.companyId();
      if (cid) {
        if (this.initialCompanyId === null) {
          this.initialCompanyId = cid;
        } else if (this.initialCompanyId !== cid) {
          if (this.sessionService.isSuperAdmin()) {
            this.router.navigate(['/app/jobs']);
            return;
          }
        }
        this.loadCompanyPricing(cid);
      }
    });
  }

  toggleTooltip(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.activeTooltipKey = this.activeTooltipKey === key ? null : key;
  }

  closeTooltip(): void {
    this.activeTooltipKey = null;
  }

  togglePricingDetails(): void {
    this.showPricingDetails = !this.showPricingDetails;
  }

  toggleService(serviceControlName: 'pickBestImage' | 'cropImages' | 'enhanceImages' | 'extractBackground'): void {
    const ctrl = this.form.get(serviceControlName);
    if (ctrl) {
      ctrl.setValue(!ctrl.value);
      ctrl.markAsDirty();
    }
  }

  ngOnInit(): void {
    if (!this.isEditMode()) {
      const activeId = this.jobProcessingState.getActiveCreateJobId();
      if (activeId) {
        this.router.navigate(['/app/jobs', activeId], { replaceUrl: true });
        return;
      }
    } else {
      this.loadJob();
    }
  }

  loadJob(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.jobsService.getJobById(this.jobId).subscribe({
      next: (response: any) => {
        this.jobStatus = response.status || '';
        this.form.patchValue(response, { emitEvent: false });
        this.updateCropsPerImagePoseValidator(!!response.cropImages);
        this.updateSmartCullValidators(!!response.pickBestImage);

        if (this.jobProcessingState.getActiveCreateJobId() !== this.jobId && this.jobStatus !== 'Draft') {
          this.form.disable({ emitEvent: false });
          // For ImageUploadCompleted, allow editing approximate subjects and crops count
          if (this.jobStatus === 'ImageUploadCompleted') {
            this.form.get('estimatedStudentsPhotographed')?.enable({ emitEvent: false });
            this.form.get('cropsPerImagePose')?.enable({ emitEvent: false });
          }
        } else {
          this.form.enable({ emitEvent: false });
        }

        this.calculateSummary();
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Failed to load job details.';
        this.isLoading = false;
      },
    });
  }

  loadCompanyPricing(companyId: number): void {
    this.companiesService.getCompanyPricing(companyId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.pricingPercentage = response.data.estimatedCostChargePercent;
          
          const pricing: any = {
            pickingCharge: 0,
            cropCharge: 0,
            enhancementCharge: 0,
            extractionCharge: 0
          };

          response.data.items.forEach(item => {
            const name = (item.service || '').toLowerCase();
            const code = (item.serviceCode || '').toLowerCase();
            
            if (name.includes('pick') || code.includes('pick')) {
              pricing.pickingCharge = item.salePrice;
            } else if (name.includes('crop') || code.includes('crop')) {
              pricing.cropCharge = item.salePrice;
            } else if (name.includes('enhance') || code.includes('enhance')) {
              pricing.enhancementCharge = item.salePrice;
            } else if (name.includes('extract') || code.includes('extract')) {
              pricing.extractionCharge = item.salePrice;
            }
          });

          this.form.patchValue({
            pickingCharge: pricing.pickingCharge ?? 0,
            cropCharge: pricing.cropCharge ?? 0,
            enhancementCharge: pricing.enhancementCharge ?? 0,
            extractionCharge: pricing.extractionCharge ?? 0,
          }, { emitEvent: true });
        }
      }
    });
  }

  calculateSummary(): void {
    const values = this.form.getRawValue();

    // 1. Total images & user-entered approximate subjects
    const noOfImages = Number(values.noOfImagesInThisJob || 0);
    const estimatedSubjects = Number(values.estimatedStudentsPhotographed || 0);

    // 2. Inverted calculation: Total Images / Approximate Subjects
    const avgFrames = (noOfImages > 0 && estimatedSubjects > 0)
      ? Number((noOfImages / estimatedSubjects).toFixed(1))
      : 0;

    // 3. Keep hidden minimumImagesIfPossible and desiredImagesPosesPerStudent in sync with imagesPerStudent
    const rawImagesToKeep = values.imagesPerStudent;
    const hasImagesToKeep = rawImagesToKeep !== null && rawImagesToKeep !== undefined;
    const imagesToKeep = hasImagesToKeep ? Number(rawImagesToKeep) : null;
    const desiredPoses = (imagesToKeep !== null && imagesToKeep > 0) ? imagesToKeep : 1;

    // 4. Extract charges
    const pickingCharge = Number(values.pickingCharge || 0);
    const cropCharge = Number(values.cropCharge || 0);
    const enhancementCharge = Number(values.enhancementCharge || 0);
    const extractionCharge = Number(values.extractionCharge || 0);
    const cropsPerPose = Number(values.cropsPerImagePose || 0);

    // 5. Calculate service costs
    const totalPickingCost = values.pickBestImage ? (pickingCharge * noOfImages) : 0;
    const totalEnhancementCost = values.enhanceImages ? (enhancementCharge * estimatedSubjects * desiredPoses) : 0;
    const totalCropCost = values.cropImages ? (cropCharge * estimatedSubjects * cropsPerPose * desiredPoses) : 0;
    const totalExtractionCost = values.extractBackground ? (extractionCharge * estimatedSubjects * desiredPoses) : 0;

    const estimatedCostOfJob = totalPickingCost + totalEnhancementCost + totalCropCost + totalExtractionCost;
    const estimatedCostPerStudent = estimatedSubjects > 0 ? (estimatedCostOfJob / estimatedSubjects) : 0;
    const upfrontCostOfJob = estimatedCostOfJob * (this.pricingPercentage / 100);

    this.form.patchValue(
      {
        avgFramesPhotographedPerStudent: avgFrames,
        minimumImagesIfPossible: imagesToKeep,
        desiredImagesPosesPerStudent: desiredPoses,
        estimatedCostOfJob: Number(estimatedCostOfJob.toFixed(2)),
        estimatedCostPerStudent: Number(estimatedCostPerStudent.toFixed(2)),
        upfrontCostOfJob: Number(upfrontCostOfJob.toFixed(2)),
      },
      { emitEvent: false },
    );
  }


  @HostListener('document:click')
  onDocumentClick(): void {
    this.activeTooltipKey = null;
  }

  onSelectFolder(): void {
    this.folderValidationMessage = '';
    if (this.folderInput) {
      this.folderInput.nativeElement.value = '';
      this.folderInput.nativeElement.click();
    }
  }

  onFolderSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files || files.length === 0) {
      return;
    }

    const allowedMimeTypes = new Set([
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
      'image/bmp',
      'image/tiff',
      'image/svg+xml',
      'image/heic',
      'image/heif',
    ]);

    const allowedExtensions = [
      '.jpg',
      '.jpeg',
      '.png',
      '.webp',
      '.gif',
      '.bmp',
      '.tif',
      '.tiff',
      '.svg',
      '.heic',
      '.heif',
    ];

    const fileArray = Array.from(files);

    // Filter to keep only valid image files (ignoring hidden files like Thumbs.db, etc.)
    const validImageFiles = fileArray.filter((file) => {
      const lowerName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some((ext) => lowerName.endsWith(ext));
      const hasValidMime = allowedMimeTypes.has(file.type);
      return hasValidExtension || hasValidMime;
    });

    if (validImageFiles.length === 0) {
      this.folderValidationMessage = 'Selected folder does not contain any valid image files.';
      return;
    }

    // Sort valid images by name numerically (e.g., 1.jpg, 2.jpg, 10.jpg)
    validImageFiles.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

    const firstFile = validImageFiles[0];
    const path = (firstFile as any)?.webkitRelativePath;
    if (path) {
      this.selectedFolderName = path.split('/')[0] || '';
    } else {
      this.selectedFolderName = `Folder with ${validImageFiles.length} images`;
    }

    this.hasVerifiedFolder = true;
    this.folderValidationMessage = '';
    this.jobsService.setAttachedFiles(validImageFiles);

    this.form.patchValue({
      noOfImagesInThisJob: validImageFiles.length,
    });
  }

  onCancel(): void {
    this.jobProcessingState.clearActiveCreateJobId();
    this.router.navigate(['/app/jobs']);
  }

  get isFolderVerificationPending(): boolean {
    return this.isEditMode() && this.jobProcessingState.getActiveCreateJobId() !== this.jobId && !this.hasVerifiedFolder;
  }

  get hasServiceSelected(): boolean {
    const v = this.form.getRawValue();
    return !!(v.pickBestImage || v.cropImages || v.enhanceImages || v.extractBackground);
  }

  onSubmit(): void {
    const errors = this.getValidationErrors();
    if (this.form.invalid || !this.hasServiceSelected || this.isFolderVerificationPending || errors.length > 0) {
      this.form.markAllAsTouched();
      this.validationAlertMessages = errors;
      this.showValidationAlert = true;
      return;
    }

    this.isSaving = true;
    const payload = this.form.getRawValue() as JobFormResponse;
    const currentCompanyId = this.companyContextService.selectedCompanyId();
    if (currentCompanyId) {
      payload.companyId = currentCompanyId;
    }

    // Explicitly attach noOfImages to satisfy backend UpdateJobRequest and CreateJobRequest schemas
    (payload as any).noOfImages = payload.noOfImagesInThisJob;

    if (this.isEditMode()) {
      this.jobsService.updateJob(this.jobId, payload).subscribe({
        next: () => {
          this.jobsService.refreshJobs();
          this.isSaving = false;
          
          const cropsCount = payload.cropsPerImagePose ?? 2;
          this.jobProcessingState.setCropsCount(this.jobId, cropsCount);

          if (payload.enhanceImages) {
            this.router.navigate(['/app/jobs', this.jobId, 'enhancement']);
          } else if (payload.cropImages) {
            this.router.navigate(['/app/jobs', this.jobId, 'crops'], {
              queryParams: { cropsCount }
            });
          } else {
            this.router.navigate(['/app/jobs', this.jobId, 'photos']);
          }
        },
        error: () => {
          this.errorMessage = 'Failed to update job.';
          this.isSaving = false;
        },
      });
    } else {
      this.jobsService.createJob(payload).subscribe({
        next: (response) => {
          this.jobsService.refreshJobs();
          this.isSaving = false;

          this.jobProcessingState.setActiveCreateJobId(response.id);

          const cropsCount = payload.cropsPerImagePose ?? 2;
          this.jobProcessingState.setCropsCount(response.id, cropsCount);

          if (payload.enhanceImages) {
            this.router.navigate(['/app/jobs', response.id, 'enhancement']);
          } else if (payload.cropImages) {
            this.router.navigate(['/app/jobs', response.id, 'crops'], {
              queryParams: { cropsCount }
            });
          } else {
            this.router.navigate(['/app/jobs', response.id, 'photos']);
          }
        },
        error: () => {
          this.errorMessage = 'Failed to create job.';
          this.isSaving = false;
        },
      });
    }
  }

  private getValidationErrors(): string[] {
    const errors: string[] = [];
    const ctrl = (name: string) => this.form.get(name);

    // Job Information
    if (ctrl('name')?.hasError('required')) {
      errors.push('Job Name is required.');
    }

    if (ctrl('noOfImagesInThisJob')?.hasError('required') || !ctrl('noOfImagesInThisJob')?.value) {
      errors.push('Please select a Job Folder containing images.');
    }

    if (ctrl('estimatedStudentsPhotographed')?.hasError('required') || !ctrl('estimatedStudentsPhotographed')?.value) {
      errors.push('Approximate Subjects is required.');
    } else if (ctrl('estimatedStudentsPhotographed')?.hasError('min')) {
      errors.push('Approximate Subjects must be at least 1.');
    }

    // Services
    if (!this.hasServiceSelected) {
      errors.push('At least one service must be selected (Smart Cull, Multi-Cropping, Enhancement, or Background Removal).');
    }

    // Multi-Cropping
    if (ctrl('cropImages')?.value) {
      const cropsVal = ctrl('cropsPerImagePose')?.value;
      if (cropsVal === null || cropsVal === undefined || (cropsVal as any) === '') {
        errors.push('Please select the number of Crops Per Image (1 to 4).');
      } else if (Number(cropsVal) < 1 || Number(cropsVal) > 4) {
        errors.push('Crops Per Image must be between 1 and 4.');
      }
    }

    // Smart Cull Settings
    if (ctrl('pickBestImage')?.value) {
      const isMissing = (v: any) => v === null || v === undefined || v === '';

      const studentVal = ctrl('imagesPerStudent')?.value;
      if (isMissing(studentVal)) {
        errors.push('Images to Keep Per Subject is required.');
      } else if (Number(studentVal) < 0) {
        errors.push('Images to Keep Per Subject cannot be negative.');
      }

      const poseVal = ctrl('imagePerUniquePose')?.value;
      if (isMissing(poseVal)) {
        errors.push('Images Per Unique Pose is required.');
      } else if (Number(poseVal) < 0) {
        errors.push('Images Per Unique Pose cannot be negative.');
      }

      const teacherVal = ctrl('imagesPerTeacherIfPossible')?.value;
      if (isMissing(teacherVal)) {
        errors.push('Images Per Teacher / Staff is required (0 is accepted).');
      } else if (Number(teacherVal) < 0) {
        errors.push('Images Per Teacher / Staff cannot be negative.');
      }

      if (ctrl('specialInstructions')?.hasError('maxlength')) {
        errors.push('Special Instructions cannot exceed 500 characters.');
      }
    }

    if (this.isFolderVerificationPending) {
      errors.push('You must select the local photos folder to attach images before proceeding.');
    }

    return errors;
  }
}
